import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Address from "./Address";
import DocumentRef from "./DocumentRef";
import {
  deepFreeze,
  formatCurrency,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import {
  cloneDeep,
  every,
  get,
  isArray,
  isEmpty,
  isNumber,
  last,
  omit,
  omitBy,
} from "lodash";
import { endOfMonth, parse } from "date-fns";
import { diff } from "deep-object-diff";

export const TRANSACTION_FIELDS = {
  // Transaction info
  date: null,
  type: "",
  memo: "",
  fromTo: [null, null],
  fromType: "",
  fromAccountType: null,
  toType: "",
  toAccountType: null,
  for: "",
  forType: "",
  amount: null,
  discount: null,
  fee: null,
  reconciled: false,
  locked: false,

  // Field payment review
  fieldCollected: false,
  fieldVerified: false,
  fieldVerifiedBy: null,

  // Payment info
  paymentType: "",
  chequeDate: "",
  chequeNumber: "",
  ccType: "",
  ccLast4: "",
  ccExp: "",
  receipt: null,
  attachment: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(TRANSACTION_FIELDS);

export const TX_TYPES = {
  "down-payment": {
    memo: "Down Payment",
    forType: "salesLead",
    fromType: "contact",
    toType: "account",
    toAccountType: "bank",
  },
  "deferred-down-payment": {
    memo: "Down Payment",
    forType: "sale",
    fromType: "contact",
    toType: "account",
    toAccountType: "bank",
  },
  "invoice-payment": {
    memo: "Invoice Payment",
    forType: "invoice",
    fromType: "contact",
    toType: "account",
    toAccountType: "bank",
  },
  "sale-payment": {
    memo: "Sale Payment",
    forType: "sale",
    fromType: "contact",
    toType: "account",
    toAccountType: "bank",
  },
  refund: {
    memo: "Refund",
    forType: "sale",
    fromType: "account",
    fromAccountType: "bank",
    toType: "contact",
  },
  "credit-refund": {
    memo: "Credit Memo Refund",
    forType: "creditMemo",
    fromType: "account",
    fromAccountType: "bank",
    toType: "contact",
  },
  "bill-payment": {
    memo: "Bill Payment",
    forType: "bill",
    fromType: "account",
    fromAccountType: "bank",
    toType: "contact",
  },
  paycheck: {
    memo: "Paycheck",
    forType: "paycheck",
    fromType: "account",
    fromAccountType: "bank",
    toType: "employee",
  },
  "vehicle-fuel": {
    memo: "Fuel",
    forType: "vehicle",
    fromType: "account",
    fromAccountType: "creditCard",
    toType: "vendor",
  },
  "vehicle-maintenance": {
    memo: "Maintenance",
    forType: "vehicle",
    fromType: "account",
    fromAccountType: "creditCard",
    toType: "vendor",
  },
  other: {},
};
deepFreeze(TX_TYPES);

export const TX_ENTITY_TYPES = {
  account: {
    label: "Account",
    icon: "account",
  },
  contact: {
    label: "Contact",
    icon: "contact",
  },
  employee: {
    label: "Employee",
    icon: "user",
  },
  vendor: {
    label: "Vendor",
    icon: "vendor",
  },
};
deepFreeze(TX_ENTITY_TYPES);

export const TX_FOR_TYPES = {
  salesLead: {
    label: "Sales Lead",
    icon: "sales_lead",
  },
  sale: {
    label: "Sale",
    icon: "sale",
  },
  bill: {
    label: "Bill",
    icon: "bill",
  },
  invoice: {
    label: "Invoice",
    icon: "invoice",
  },
  creditMemo: {
    label: "Credit Memo",
    icon: "credit_memo",
  },
  paycheck: {
    label: "Paycheck",
    icon: "cheque",
  },
  vehicle: {
    label: "Vehicle",
    icon: "vehicle",
  },
};
deepFreeze(TX_ENTITY_TYPES);

export const TX_PAYMENT_TYPES = {
  cash: {
    label: "Cash",
    icon: "cash",
  },
  cheque: {
    label: "Cheque",
    icon: "cheque",
  },
  cc: {
    label: "Credit Card",
    icon: "credit_card",
  },
};
deepFreeze(TX_PAYMENT_TYPES);

export const TX_CARD_TYPES = [
  "American Express",
  "Discover",
  "MasterCard",
  "VISA",
];
deepFreeze(TX_CARD_TYPES);

export default class Transaction extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, TRANSACTION_FIELDS);

    // Convert to Date
    data.date = timestampToDate(data.date);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: Transaction.canBeViewedBy,
      create: Transaction.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
    });

    // Documents
    if (this.data.receipt) {
      this.data.receipt = this.receipt.data;
    }
    if (this.data.attachment) {
      this.data.attachment = this.attachment.data;
    }
  }

  setType(typeID) {
    const type = TX_TYPES[typeID];
    if (!type) {
      throw "Invalid transaction type";
    } else {
      const data = omitBy(
        {
          type: typeID,
          ...type,
        },
        isArray
      );
      Object.assign(this.data, data);
    }
    return this;
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isEmployee;
  }

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    // Only admins and accounting can edit down payments once an invoice is created.
    // The back-end reallocates downPaymentAmount across active invoices on change.
    if (
      this.isDownPayment &&
      this.hasInvoice &&
      !(user.isAdmin || (user.isEmployee && user.hasRole("accounting")))
    ) {
      return false;
    }
    return (
      user.isAdmin ||
      (!this.locked &&
        user.isEmployee &&
        (this.data.createdBy === user.id ||
          (user.hasRole && user.hasRole("accounting"))))
    );
  }

  get isDownPayment() {
    return ["down-payment", "deferred-down-payment"].includes(this.data.type);
  }

  get hasInvoice() {
    const sale = this.relatedSale;
    return sale && Object.keys(sale.data.status.invoices || {}).length > 0;
  }

  get relatedSale() {
    if (this.data.forType === "sale") {
      // for field contains projectID + saleID
      return store.state.main.sales[this.data.for];
    } else if (this.data.forType === "salesLead") {
      // for field contains salesLead ID, need to find the sale via the lead
      const lead = store.state.main.salesLeads[this.data.for];
      if (lead && lead.data.projectID && lead.data.saleID) {
        return store.state.main.sales[lead.data.projectID + lead.data.saleID];
      }
    }
    return null;
  }

  canBeDeletedBy(user) {
    // Only admins and accounting can delete down payments once an invoice is created.
    // The back-end reallocates downPaymentAmount across active invoices on delete.
    if (
      this.isDownPayment &&
      this.hasInvoice &&
      !(user.isAdmin || (user.isEmployee && user.hasRole("accounting")))
    ) {
      return false;
    }
    return (
      !this.locked &&
      (user.isAdmin || (user.isEmployee && this.data.createdBy === user.id))
    );
  }

  canBeUnlockedBy(user) {
    return !this.data.reconciled && user.isAdmin;
  }

  // Database

  get path() {
    return Transaction.getPath(this.id);
  }

  static getPath(id) {
    let path = `tx`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(tx) {
    return tx.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Transaction(snapshot.id, data);
  }

  // Editing

  get rules() {
    const required = (a) => Boolean(a);
    const validEntityType = (t) => t in TX_ENTITY_TYPES;
    return {
      date: [(a) => required(a) || "required"],
      "fromTo[0]": [(a) => required(a) || "required"],
      fromType: [
        (a) => required(a) || "required",
        (t) => validEntityType(t) || "sourceTypeInvalid",
      ],
      "fromTo[1]": [(a) => required(a) || "required"],
      toType: [
        (a) => required(a) || "required",
        (t) => validEntityType(t) || "destinationTypeInvalid",
      ],
      for: [(a) => required(a) || "required"],
      forType: [
        (a) => required(a) || "required",
        (t) => t in TX_FOR_TYPES || "forTypeInvalid",
      ],
      type: [
        (a) => required(a) || "required",
        (t) => t in TX_TYPES || "transactionTypeInvalid",
      ],
      amount: [
        (a) => required(a) || "required",
        (a) => {
          const limit = this.creditRefundLimit;
          return (
            limit === null || roundPrice(a) <= limit || "refundExceedsCredit"
          );
        },
      ],
      discount: [(a) => !a || a > 0 || "discountPositive"],
      receipt: [
        (a) => required(!a || this.receipt.isValid) || "receiptInvalid",
      ],
      attachment: [
        (a) => required(!a || this.attachment.isValid) || "attachmentInvalid",
      ],
      paymentType: [
        (a) =>
          (this.data.fromType === "account" &&
            this.data.toType === "account") ||
          required(a) ||
          "required",
      ],
      chequeDate: [
        (a) => this.data.paymentType !== "cheque" || required(a) || "required",
      ],
      chequeNumber: [
        (a) => this.data.paymentType !== "cheque" || required(a) || "required",
      ],
      fee: [
        (a) =>
          this.data.paymentType !== "cc" ||
          (isNumber(a) && a >= 0) ||
          "feeInvalid",
      ],
      ccType: [
        (a) => this.data.paymentType !== "cc" || required(a) || "required",
      ],
      ccLast4: [
        (a) =>
          this.data.paymentType !== "cc" ||
          (required(a) && a.length === 4) ||
          "required",
      ],
      ccExp: [
        (a) => {
          if (this.data.paymentType !== "cc") {
            return true;
          }
          const now = new Date();
          return (
            (required(a) &&
              /\d\d\/\d\d/.test(a) &&
              endOfMonth(parse(a, "MM/yy", now)) >= now) ||
            "ccExpInvalid"
          );
        },
      ],
    };
  }

  get isValid() {
    const data = Transaction.sanitize(cloneDeep(this.data));
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(data, key)) === true)
    );
  }

  get isEmpty() {
    return Boolean(
      !this.id &&
        isEmpty(diff(omit(this.data, "date"), omit(TRANSACTION_FIELDS, "date")))
    );
  }

  isEqual(tx) {
    return Boolean(
      this.id === tx.id &&
        isEmpty(
          diff(
            Transaction.sanitize(cloneDeep(this.data)),
            Transaction.sanitize(cloneDeep(tx.data))
          )
        )
    );
  }

  get isCustomerPayment() {
    return [
      "deferred-down-payment",
      "down-payment",
      "invoice-payment",
      "sale-payment",
    ].includes(this.data.type);
  }

  get needsReview() {
    return Boolean(this.data.fieldCollected && !this.data.fieldVerified);
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.receipt) {
      const doc = new DocumentRef(data.receipt);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.receipt = null;
      }
    }
    if (data.attachment) {
      const doc = new DocumentRef(data.attachment);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.attachment = null;
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    if (data.paymentType !== "cc") {
      data.ccType = "";
      data.ccLast4 = "";
      data.ccExp = "";
    } else if (data.paymentType !== "cheque") {
      data.chequeDate = "";
      data.chequeNumber = "";
    }
    Transaction.cleanDocuments(data, removeUnsaved);
    return data;
  }

  // Formatting

  get type() {
    return this.data.type ? TX_TYPES[this.data.type] : null;
  }

  get paymentType() {
    return TX_PAYMENT_TYPES[this.data.paymentType];
  }

  get ccLast4() {
    return "**** **** **** " + this.data.ccLast4;
  }

  get ccExpDate() {
    return endOfMonth(parse(this.data.ccExp, "MM/yy", new Date()));
  }

  get amount() {
    return formatCurrency(
      this.data.amount - this.data.discount + this.data.fee
    );
  }

  get discount() {
    return formatCurrency(this.data.discount);
  }

  get fee() {
    return formatCurrency(this.data.fee);
  }

  get from() {
    switch (this.data.fromType) {
      case "account":
        return store.state.main.accounts[this.data.fromTo[0]];
      case "contact":
        return new Address(this.data.fromTo[0]);
      default:
        return store.state.main.contacts[this.data.fromTo[0]] || null;
    }
  }

  get to() {
    switch (this.data.toType) {
      case "account":
        return store.state.main.accounts[this.data.fromTo[1]];
      case "contact":
        return new Address(this.data.fromTo[1]);
      default:
        return store.state.main.contacts[this.data.fromTo[1]] || null;
    }
  }

  get for() {
    switch (this.data.forType) {
      case "salesLead":
        return store.state.main.salesLeads[this.data.for];
      case "sale":
        return store.state.main.sales[this.data.for];
      case "invoice":
        return store.state.main.invoices[last(this.data.for.split("-"))];
      case "creditMemo":
        return store.state.main.creditMemos[last(this.data.for.split("-"))];
      case "bill":
        return store.state.main.bills[last(this.data.for.split("-"))];
      case "paycheck":
        return store.state.main.paychecks[last(this.data.for.split("-"))];
      case "vehicle":
        return store.state.main.vehicles[this.data.for];
    }
  }

  // The most a credit refund can be: what its credit memo has neither on an
  // invoice nor already refunded, plus this transaction's own amount when it
  // is already posted to that memo. Null when this isn't a credit refund or
  // the memo isn't loaded.
  get creditRefundLimit() {
    if (this.data.forType !== "creditMemo") return null;
    const creditMemo = this.for;
    if (!creditMemo) return null;
    const posted = this.id ? store.state.main.transactions[this.id] : null;
    const ownAmount =
      posted && posted.data.for === this.data.for
        ? Number(posted.data.amount) || 0
        : 0;
    return roundPrice(creditMemo.unappliedAmount + ownAmount);
  }

  get receipt() {
    return this.data.receipt ? new DocumentRef(this.data.receipt) : null;
  }

  get attachment() {
    return this.data.attachment ? new DocumentRef(this.data.attachment) : null;
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  toString() {
    return this.data.memo;
  }
}
